import { staleTimes, useQuery } from '@platform/api-client';
import { browserTimeZone, type ChartPoint } from '@platform/design-system';
import type { Operation, ResponseBody, TypedClient } from '@platform/openapi';
import { moneyPointOf } from '../../chart-points.ts';
import type { ReportPeriod, SeriesUnit } from '../../report-period.ts';
import { queryKeys, usePageContext } from '../../usePageContext.ts';

export type ProductStatisticsReport = ResponseBody<Operation<'/admin/products/{id}/statistics', 'get'>>;

export const PRODUCT_CHART_METRICS = ['revenue', 'losses'] as const;
export type ProductChartMetric = (typeof PRODUCT_CHART_METRICS)[number];

const NOTHING = '0';

export const useProductStatistics = (
  api: TypedClient,
  productId: string,
  period: ReportPeriod,
  unit: SeriesUnit,
) => {
  const { tenantId } = usePageContext();
  const query = { ...period, unit, timeZone: browserTimeZone() };
  return useQuery({
    queryKey: queryKeys.productStatistics(tenantId, productId, query),
    queryFn: () => api.get('/admin/products/{id}/statistics', { path: { id: productId }, query }),
    staleTime: staleTimes.dashboard,
  });
};

export const productChartPointsOf = (
  report: ProductStatisticsReport,
  metric: ProductChartMetric,
  unit: SeriesUnit,
  locale: string,
): ChartPoint[] => {
  const scale = { unit, timeZone: browserTimeZone(), locale };
  return report.series.map((point) =>
    moneyPointOf(
      point.bucket,
      { amount: metric === 'revenue' ? point.revenue : (point.losses ?? NOTHING), currency: report.currency },
      scale,
    ),
  );
};
